"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { ShoppingCart, ExternalLink } from "lucide-react"
import { mapBlogToProductCategory } from "@/lib/blog-product-mapping"
import { contentAnalytics } from "@/lib/analytics"
import { getDirectusImageUrl } from "@/lib/directus"
import { PickBadgePill } from "@/components/blog/pick-badge-pill"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { deriveShortName } from "@/lib/affiliate-cta"
import { isCommercialIntent } from "@/lib/blog-intent"

interface CategoryProductCalloutProps {
  variant?: string // blog category
  slug?: string
  tags?: string[]
}

interface ProductData {
  name: string
  description: string
  price: number | string
  affiliate_url: string
  image?: string
  category?: string
}

export function CategoryProductCallout({ variant = "", slug = "", tags }: CategoryProductCalloutProps) {
  const [product, setProduct] = useState<ProductData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetchProduct() {
      try {
        const category = mapBlogToProductCategory(variant, slug)

        const params = new URLSearchParams({
          category,
          limit: "1",
        })
        if (slug) params.set("slug", slug)
        if (tags && tags.length > 0) params.set("tags", tags.join(","))

        const response = await fetch(`/api/blog/product-recommendations?${params}`)
        if (!response.ok) return

        const data = await response.json()
        if (!cancelled && data.products?.length > 0) {
          setProduct(data.products[0])
        }
      } catch {
        // Silently fail - no product is better than a broken UI
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    const timer = setTimeout(fetchProduct, 150)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [variant, slug, tags])

  // Unified affiliate telemetry surface for this early category callout. Hook runs
  // unconditionally (rules-of-hooks); the ref only attaches to the real card below,
  // so the impression fires when the loaded card scrolls into view, not the loader.
  const surface = "blog-callout-early"
  // Position mirrors the server-side placement in insertProductPickMarkers: the
  // generic fallback card sits ABOVE H2 #1 (lead) on commercial-intent posts and
  // after the first section (body) on informational posts. Emit the real slot so
  // the lead-vs-body CTR split this surface measures stays honest — a flat "lead"
  // mislabeled every informational card. product_source keeps this category-API
  // card distinct from the curated blog-callout picks.
  const position = isCommercialIntent(slug) ? "lead" : "body"
  const impressionRef = useAffiliateImpression(surface, {
    slug,
    category: variant,
    position,
    product_source: "category-api",
    // Mirrors the value affiliateLinkClicked already sends. Without it the
    // impression lands under a null placement while the click lands under
    // "callout-early", so placement-level CTR reads as 0 shown / n clicked.
    placement: "callout-early",
    // One impression per rendered card set from the category API.
    denominator: 'container',
  })

  if (isLoading) {
    // Reserve vertical space so the card popping in after the async fetch doesn't
    // shift the article text below it (CLS) now that this callout sits early in the post.
    return <div aria-hidden className="my-8 min-h-[220px]" />
  }
  if (!product) return null

  const imageUrl = product.image ? getDirectusImageUrl(product.image, 224, 224) : null

  // Honest badge: this product is the top RELEVANCE MATCH the API returned for the
  // category (scored by keyword relevance → best-seller → newest), not a product the
  // team hand-selected. "Our Pick" overclaimed an editorial endorsement; "Top {cat}
  // match" describes what it actually is. Derive a short, grammatical category word
  // from the blog category (variant) or the product's own category.
  const rawCategory = (variant || product.category || "").split(/\s*(?:&| and )\s*/i)[0].trim()
  const categoryBadgeLabel = rawCategory ? `Top ${rawCategory} match` : "Top match"
  const ctaProductLabel = deriveShortName(product.name)

  const handleClick = () => {
    contentAnalytics.affiliateLinkClicked(slug, product.name, product.affiliate_url || "", "callout-early")
    contentAnalytics.affiliateProductClicked(surface, {
      productName: product.name,
      affiliateLink: product.affiliate_url || "",
      position,
      product_source: "category-api",
      placement: "callout-early",
    })
  }

  return (
    <div ref={impressionRef} className="my-8 rounded-[22px] border border-gray-200 bg-white p-5 sm:p-[26px] shadow-[0_16px_44px_-22px_rgba(18,46,30,0.28)]">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-[26px]">
        <div className="max-sm:h-[200px] w-full flex-shrink-0 overflow-hidden rounded-2xl bg-[#f3f6f3] flex items-center justify-center sm:aspect-square sm:w-[170px]">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={product.name}
              width={340}
              height={340}
              loading="lazy"
              className="h-full w-full object-contain p-3"
            />
          ) : (
            <ShoppingCart className="h-10 w-10 text-green-700/70" />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-stretch gap-3 sm:items-start">
          {/* Wrapper keeps the inline-flex pill content-sized: it's a direct
              child of an `items-stretch` column, so without this it stretches
              full-width on mobile. Mirrors inline-product-callout.tsx. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <PickBadgePill badge={{ label: categoryBadgeLabel, tone: "green", tier: "pick" }} showStar />
          </div>
          <p className="font-editorial text-[22px] font-semibold leading-[1.18] tracking-[-0.01em] text-gray-900 text-balance sm:text-2xl">
            {product.name}
          </p>
          <p className="text-[15px] leading-relaxed text-gray-700">{product.description}</p>
          <a
            href={product.affiliate_url || "#"}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            data-affiliate="true"
            data-affiliate-placement="card-v1"
            onClick={handleClick}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-[26px] min-h-[52px] text-base font-bold text-white! hover:text-white! shadow-[0_12px_26px_-10px_rgba(22,163,74,0.5)] transition hover:-translate-y-0.5 active:scale-[0.98] no-underline hover:no-underline sm:w-auto"
          >
            <span className="text-balance">See {ctaProductLabel} on Amazon</span>
            <ExternalLink className="h-[17px] w-[17px] flex-shrink-0" />
          </a>
          <p className="text-xs leading-normal text-gray-500">
            As an Amazon Associate we earn from qualifying purchases.
          </p>
        </div>
      </div>
    </div>
  )
}
